import { formatDistanceToNow } from "date-fns"

import {
  BUCKET_INTERVAL,
  type BucketInterval,
} from "@/utils/date/timeBucketing"
import { canonicalizeTimezone } from "@/utils/date/timezone"

/**
 * Get browser's current timezone
 */
export const getBrowserTimezone = (): string => {
  return canonicalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
}

/**
 * Get timezone abbreviation (e.g., PST, PDT, EST)
 */
const getTimezoneAbbreviation = (date: Date, timezone: string): string => {
  try {
    const formatted = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).format(date)

    // Extract abbreviation (last word)
    const parts = formatted.split(" ")
    return parts[parts.length - 1]
  } catch {
    // Fallback to offset format
    const offset = date.toLocaleString("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    })
    return offset.split(" ").pop() || "UTC"
  }
}

/**
 * Check if date is in DST for given timezone
 */
const isInDST = (date: Date, timezone: string): boolean => {
  const jan = new Date(date.getFullYear(), 0, 1)
  const jul = new Date(date.getFullYear(), 6, 1)

  const getOffset = (d: Date) => {
    const str = d.toLocaleString("en-US", {
      timeZone: timezone,
      hour12: false,
    })
    return new Date(str).getTime() - d.getTime()
  }

  const janOffset = getOffset(jan)
  const julOffset = getOffset(jul)
  const dateOffset = getOffset(date)

  return Math.min(janOffset, julOffset) === dateOffset
}

/**
 * Format bucket time range with timezone
 */
export const formatBucketTimeRange = (
  startTime: Date | string,
  endTime: Date | string,
  bucketInterval: BucketInterval,
  timezone?: string,
): string => {
  const tz = canonicalizeTimezone(timezone || getBrowserTimezone())
  const start = typeof startTime === "string" ? new Date(startTime) : startTime
  const end = typeof endTime === "string" ? new Date(endTime) : endTime

  const tzAbbr = getTimezoneAbbreviation(start, tz)

  // For daily buckets, show full day range
  if (bucketInterval === BUCKET_INTERVAL.ONE_DAY) {
    const dateStr = start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: tz,
    })
    const startStr = start.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: tz,
    })
    const endStr = end.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: tz,
    })
    return `${dateStr}\n${startStr} - ${endStr} ${tzAbbr}`
  }

  // For sub-daily buckets
  const startDay = start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: tz,
  })
  const endDay = end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: tz,
  })
  const startTimeStr = start.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: start.getMinutes() === 0 ? undefined : "2-digit",
    hour12: true,
    timeZone: tz,
  })
  const endTimeStr = end.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: end.getMinutes() === 0 ? undefined : "2-digit",
    hour12: true,
    timeZone: tz,
  })

  // Same day
  if (startDay === endDay) {
    return `${startDay}, ${startTimeStr} - ${endTimeStr} ${tzAbbr}`
  }

  // Crosses midnight
  return `${startDay}, ${startTimeStr} - ${endDay}, ${endTimeStr} ${tzAbbr}`
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (date: Date | string): string => {
  const d = typeof date === "string" ? new Date(date) : date
  return formatDistanceToNow(d, { addSuffix: true })
}

/**
 * Check if bucket crosses DST boundary
 */
export const checkDSTTransition = (
  startTime: Date,
  endTime: Date,
  timezone: string,
): { isDSTTransition: boolean; message?: string } => {
  const startDST = isInDST(startTime, timezone)
  const endDST = isInDST(endTime, timezone)

  if (startDST !== endDST) {
    return {
      isDSTTransition: true,
      message: startDST
        ? "Clock moved back 1 hour"
        : "Clock moved forward 1 hour",
    }
  }

  return { isDSTTransition: false }
}
